import { Button } from "@nimit9/signet-ui/components/button"
import { Input } from "@nimit9/signet-ui/components/input"
import { Label } from "@nimit9/signet-ui/components/label"
import { useQuery } from "@tanstack/react-query"
import { ShieldCheckIcon } from "lucide-react"
import { useState } from "react"
import { FirstImportChoices } from "@/components/common/first-import-choices"
import { MnemonicDisplay } from "@/components/common/mnemonic-display"
import { PinInput, weakPinReason } from "@/components/common/pin-input"
import { type AuthCryptoData, useSetEncryptionMode } from "@/hooks/use-auth-crypto"
import { requestEncryptionBackfill } from "@/hooks/use-backfill-encryption"
import { useCreateHousehold, useUpdateHousehold } from "@/hooks/use-household"
import { api } from "@/lib/api"
import {
  deriveKEK,
  generateDEK,
  generateKdfSalt,
  generateRecoveryMnemonic,
  recoveryKeyFromMnemonic,
  wrapDEK,
  wrapKeyForRecovery,
} from "@/lib/crypto"
import { startInactivityWatcher, useCryptoStore } from "@/store/crypto"

// ─── Step interface ───────────────────────────────────────────────────────────
export interface OnboardingStepProps {
  onNext: () => void
  onSkip: () => void
}

// ─── Step config ──────────────────────────────────────────────────────────────
// Add, remove, or reorder steps here. Each entry is a label + component.
export const ONBOARDING_STEPS: { label: string; component: React.FC<OnboardingStepProps> }[] = [
  { label: "Household", component: HouseholdStep },
  { label: "Owners", component: OwnersStep },
  { label: "Account", component: AccountStep },
  { label: "Privacy", component: PrivacyStep },
  { label: "Import", component: FirstImportStep },
]

// ─── Step 1: Create Household ─────────────────────────────────────────────────
function HouseholdStep({ onNext }: OnboardingStepProps) {
  const [name, setName] = useState("")
  const [error, setError] = useState<string | null>(null)
  const createHousehold = useCreateHousehold()

  async function handleCreate() {
    setError(null)
    try {
      await createHousehold.mutateAsync({ name: name.trim() || undefined })
      onNext()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create household")
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Name your household</h2>
        <p className="text-sm text-text-muted mt-1">This groups your family's finances together.</p>
      </div>
      <div className="space-y-2">
        <Label>Household name</Label>
        <Input
          placeholder="e.g. The Mehta Family"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          autoFocus
        />
        <p className="text-xs text-text-muted">Leave blank to use a default name.</p>
      </div>
      {error && <p className="text-sm text-accent-red">{error}</p>}
      <Button onClick={handleCreate} disabled={createHousehold.isPending} className="w-full">
        {createHousehold.isPending ? "Creating..." : "Create household"}
      </Button>
    </div>
  )
}

// ─── Step 2: Set up owners ────────────────────────────────────────────────────
function OwnersStep({ onNext, onSkip }: OnboardingStepProps) {
  const [owners, setOwners] = useState<{ key: string; label: string }[]>([
    { key: "user_1", label: "" },
  ])
  const [error, setError] = useState<string | null>(null)
  const updateHousehold = useUpdateHousehold()

  function addOwner() {
    const nextKey = `user_${owners.filter((o) => o.key !== "joint").length + 1}`
    setOwners([...owners, { key: nextKey, label: "" }])
  }

  function removeOwner(index: number) {
    if (owners.length <= 1) return
    setOwners(owners.filter((_, i) => i !== index))
  }

  function updateLabel(index: number, label: string) {
    setOwners(owners.map((o, i) => (i === index ? { ...o, label } : o)))
  }

  async function handleSave() {
    const labels: Record<string, string> = {}
    for (const o of owners) {
      if (o.label.trim()) {
        const slug = o.label.trim().toLowerCase().replace(/\s+/g, "_")
        labels[slug] = o.label.trim()
      }
    }
    // Always add joint
    labels.joint = "Joint"

    if (Object.keys(labels).length < 2) {
      setError("Add at least one person")
      return
    }

    setError(null)
    try {
      await updateHousehold.mutateAsync({ ownerLabels: labels })
      onNext()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save owners")
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Who manages money?</h2>
        <p className="text-sm text-text-muted mt-1">
          Add everyone who tracks finances. You can change this later in settings.
        </p>
      </div>
      <div className="space-y-2">
        {owners.map((owner, i) => (
          <div key={owner.key} className="flex items-center gap-2">
            <Input
              placeholder={`Person ${i + 1}`}
              value={owner.label}
              onChange={(e) => updateLabel(i, e.target.value)}
              autoFocus={i === 0}
            />
            {owners.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => removeOwner(i)}
              >
                ×
              </Button>
            )}
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addOwner}>
          + Add person
        </Button>
      </div>
      <p className="text-xs text-text-muted">"Joint" is added automatically for shared expenses.</p>
      {error && <p className="text-sm text-accent-red">{error}</p>}
      <div className="flex gap-2">
        <Button variant="ghost" onClick={onSkip} className="flex-1">
          Skip
        </Button>
        <Button onClick={handleSave} disabled={updateHousehold.isPending} className="flex-1">
          {updateHousehold.isPending ? "Saving..." : "Continue"}
        </Button>
      </div>
    </div>
  )
}

// ─── Step 3: Add first bank account ──────────────────────────────────────────
function AccountStep({ onNext, onSkip }: OnboardingStepProps) {
  const [form, setForm] = useState({
    name: "",
    bankName: "",
    type: "savings",
    last4: "",
    owner: "user_1",
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleAdd() {
    if (!form.name.trim() || !form.bankName.trim() || !form.last4.trim()) {
      setError("Fill in all fields")
      return
    }
    setError(null)
    setSaving(true)
    try {
      await api.post("/accounts", form)
      onNext()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add account")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Add your first account</h2>
        <p className="text-sm text-text-muted mt-1">
          A bank account or credit card to start tracking transactions.
        </p>
      </div>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label>Account name</Label>
          <Input
            placeholder="e.g. HDFC Savings"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Bank</Label>
            <Input
              placeholder="e.g. HDFC"
              value={form.bankName}
              onChange={(e) => setForm({ ...form, bankName: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Last 4 digits</Label>
            <Input
              placeholder="1234"
              maxLength={4}
              value={form.last4}
              onChange={(e) => setForm({ ...form, last4: e.target.value })}
            />
          </div>
        </div>
      </div>
      {error && <p className="text-sm text-accent-red">{error}</p>}
      <div className="flex gap-2">
        <Button variant="ghost" onClick={onSkip} className="flex-1">
          Skip
        </Button>
        <Button onClick={handleAdd} disabled={saving} className="flex-1">
          {saving ? "Adding..." : "Add account"}
        </Button>
      </div>
    </div>
  )
}

// ─── Step 4: Privacy mode ────────────────────────────────────────────────────
function PrivacyStep({ onNext, onSkip }: OnboardingStepProps) {
  type Phase = "choose" | "pin" | "recovery"
  const [phase, setPhase] = useState<Phase>("choose")
  const [pin, setPin] = useState("")
  const [confirmPin, setConfirmPin] = useState("")
  const [mnemonic, setMnemonic] = useState("")
  const [pendingKey, setPendingKey] = useState<CryptoKey | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [phraseSaved, setPhraseSaved] = useState(false)

  const setEncryptionMode = useSetEncryptionMode()
  const { setKey, setMode: setStoreMode } = useCryptoStore()

  useQuery({
    queryKey: ["profile"],
    queryFn: () => api.get<{ data: { id: string } }>("/profile"),
  })

  async function handleSetPin() {
    if (pin.length !== 6) {
      setError("PIN must be 6 digits")
      return
    }
    if (pin !== confirmPin) {
      setError("PINs do not match")
      return
    }
    const weak = weakPinReason(pin)
    if (weak) {
      setError(weak)
      return
    }
    setError(null)
    setLoading(true)
    try {
      // Never mint over existing keys (#244 L3): a household that already set up private
      // mode reached this wizard by mistake, and a new DEK would orphan its data.
      const current = await api.get<AuthCryptoData>("/auth-crypto")
      if (current.wrappedDek || current.encryptedVerifier || current.encryptedRecoveryKey) {
        setError("Privacy mode is already set up. Turn it on from Settings → Privacy.")
        return
      }
      // v2: random DEK + Argon2id KEK
      const dek = await generateDEK()
      const kdfSalt = generateKdfSalt()
      const kek = await deriveKEK(pin, kdfSalt)
      const wrappedDek = await wrapDEK(dek, kek)
      const phrase = generateRecoveryMnemonic()
      const recoveryKey = await recoveryKeyFromMnemonic(phrase)
      const wrappedKey = await wrapKeyForRecovery(dek, recoveryKey)
      await setEncryptionMode.mutateAsync({
        encryptionMode: "private",
        encryptedRecoveryKey: wrappedKey,
        kdfVersion: 2,
        kdfSalt,
        wrappedDek,
      })
      setPendingKey(dek)
      setMnemonic(phrase)
      setPhase("recovery")
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to set up PIN")
    } finally {
      setLoading(false)
    }
  }

  async function handleDone() {
    if (!pendingKey) {
      setError("Something went wrong — please go back and try again")
      return
    }
    requestEncryptionBackfill()
    await setKey(pendingKey)
    setStoreMode("private")
    startInactivityWatcher()
    onNext()
  }

  // ── Choose ────────────────────────────────────────────────────────────────
  if (phase === "choose") {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-green/10">
            <ShieldCheckIcon className="h-6 w-6 text-accent-green" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Privacy mode</h2>
            <p className="text-sm text-text-muted mt-1">
              Encrypt your financial data with a PIN. Not even the app owner can see your
              transactions, budgets, or balances.
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-border-subtle bg-bg-elevated px-4 py-3 space-y-1.5">
          <p className="text-[12px] text-text-secondary font-medium">What privacy mode does:</p>
          <ul className="text-[12px] text-text-muted space-y-1">
            <li>• Encrypts transaction descriptions, notes, and references</li>
            <li>• Uses a 6-digit PIN — we never store or see it</li>
            <li>• A 12-word recovery phrase lets you reset your PIN</li>
          </ul>
        </div>
        <Button onClick={() => setPhase("pin")} className="w-full">
          Enable privacy mode
        </Button>
        <Button variant="ghost" onClick={onSkip} className="w-full">
          Skip — keep standard mode
        </Button>
      </div>
    )
  }

  // ── PIN entry ─────────────────────────────────────────────────────────────
  if (phase === "pin") {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Set your PIN</h2>
          <p className="text-sm text-text-muted mt-1">Choose a 6-digit PIN to protect your data.</p>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-text-muted text-center">PIN</p>
            <PinInput value={pin} onChange={setPin} autoFocus />
          </div>
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-text-muted text-center">
              Confirm PIN
            </p>
            <PinInput value={confirmPin} onChange={setConfirmPin} />
          </div>
        </div>
        {error && <p className="text-caption text-accent-red text-center">{error}</p>}
        <div className="flex gap-2">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={() => {
              setPhase("choose")
              setPin("")
              setConfirmPin("")
              setError(null)
            }}
          >
            Back
          </Button>
          <Button
            className="flex-1"
            onClick={handleSetPin}
            disabled={loading || pin.length !== 6 || confirmPin.length !== 6}
          >
            {loading ? "Setting up…" : "Set PIN"}
          </Button>
        </div>
      </div>
    )
  }

  // ── Recovery phrase ───────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Save your recovery phrase</h2>
        <p className="text-sm text-text-muted mt-1">
          Write these 12 words down. You'll need them if you forget your PIN.
        </p>
      </div>
      <MnemonicDisplay mnemonic={mnemonic} />
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={phraseSaved}
          onChange={(e) => setPhraseSaved(e.target.checked)}
          className="h-4 w-4 rounded border-border-default"
        />
        <span className="text-[13px] text-text-secondary">
          I've written down my recovery phrase
        </span>
      </label>
      {error && <p className="text-caption text-accent-red text-center">{error}</p>}
      <Button onClick={handleDone} disabled={!phraseSaved} className="w-full">
        Done — go to dashboard
      </Button>
    </div>
  )
}

// ─── Final step: bring the first month in ─────────────────────────────────────
// Replaces the old "Done → dashboard" step: onboarding ends in an import, not
// an empty dashboard (PAISA-ING-01). Every path below is optional — the person
// can always finish with nothing imported and do it later from the dashboard's
// empty state, which shows the same three choices.
function FirstImportStep({ onNext }: OnboardingStepProps) {
  return (
    <div className="space-y-4 text-center">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Bring your first month in</h2>
        <p className="text-sm text-text-muted mt-1">
          Pick one — you can always do the others later from the dashboard.
        </p>
      </div>
      <div className="flex justify-center">
        <FirstImportChoices />
      </div>
      <Button variant="ghost" onClick={onNext} className="w-full">
        Skip for now — go to dashboard
      </Button>
    </div>
  )
}
